import React, { useState, useEffect, useCallback, useRef } from "react";
import styled from "styled-components";
import { ThemeProvider } from "styled-components";
import { token } from "../../../../../theme";

// Tetris piece definitions
const TETROMINOS = {
  I: {
    shape: [[1, 1, 1, 1]],
    color: "#00f0f0",
  },
  O: {
    shape: [
      [1, 1],
      [1, 1],
    ],
    color: "#f0f000",
  },
  T: {
    shape: [
      [0, 1, 0],
      [1, 1, 1],
    ],
    color: "#a000f0",
  },
  S: {
    shape: [
      [0, 1, 1],
      [1, 1, 0],
    ],
    color: "#00f000",
  },
  Z: {
    shape: [
      [1, 1, 0],
      [0, 1, 1],
    ],
    color: "#f00000",
  },
  J: {
    shape: [
      [1, 0, 0],
      [1, 1, 1],
    ],
    color: "#0000f0",
  },
  L: {
    shape: [
      [0, 0, 1],
      [1, 1, 1],
    ],
    color: "#f0a000",
  },
};

const BOARD_WIDTH = 10;
const BOARD_HEIGHT = 20;

interface Piece {
  shape: number[][];
  color: string;
  x: number;
  y: number;
}

const GameContainer = styled.div`
  padding: 16px;
  background: ${(props) => props.theme.MSBackground};
  display: flex;
  gap: 20px;
  align-items: flex-start;
  min-width: 500px;
`;

const GameBoard = styled.div`
  display: grid;
  grid-template-columns: repeat(${BOARD_WIDTH}, 25px);
  gap: 1px;
  background: ${(props) => props.theme.darkShadowColor};
  padding: 4px;
  box-shadow: ${(props) => props.theme.invertedStandardBoxShadow};
`;

const Cell = styled.div<{ filled: boolean; color?: string }>`
  width: 25px;
  height: 25px;
  background: ${(props) =>
    props.filled ? props.color || "#666" : props.theme.MSBackground};
  border: ${(props) =>
    props.filled ? "1px solid rgba(255,255,255,0.3)" : "none"};
  box-sizing: border-box;
`;

const GameInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 120px;
`;

const ScoreDisplay = styled.div`
  background: ${(props) => props.theme.black};
  color: ${(props) => props.theme.white};
  padding: 8px;
  font-family: monospace;
  font-size: 14px;
  box-shadow: ${(props) => props.theme.invertedStandardBoxShadow};
  text-align: center;
`;

const NextPieceContainer = styled.div`
  background: ${(props) => props.theme.MSBackground};
  box-shadow: ${(props) => props.theme.invertedStandardBoxShadow};
  padding: 8px;
  text-align: center;
`;

const NextPieceGrid = styled.div<{ size: number }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.size}, 20px);
  gap: 1px;
  margin-top: 8px;
  justify-content: center;
`;

const NextPieceCell = styled.div<{ filled: boolean; color?: string }>`
  width: 20px;
  height: 20px;
  background: ${(props) =>
    props.filled ? props.color || "#666" : "transparent"};
  border: ${(props) =>
    props.filled ? "1px solid rgba(255,255,255,0.3)" : "1px solid #666"};
  box-sizing: border-box;
`;

const ControlButton = styled.button`
  background: ${(props) => props.theme.MSBackground};
  box-shadow: ${(props) => props.theme.standardBoxShadow};
  border: none;
  padding: 8px 16px;
  cursor: pointer;
  font-size: ${(props) => props.theme.fontNormal}px;

  &:active {
    box-shadow: ${(props) => props.theme.invertedStandardBoxShadow};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Tetris: React.FC = () => {
  const [board, setBoard] = useState<(string | null)[][]>(() =>
    Array(BOARD_HEIGHT)
      .fill(null)
      .map(() => Array(BOARD_WIDTH).fill(null))
  );

  const [currentPiece, setCurrentPiece] = useState<Piece | null>(null);
  const [nextPiece, setNextPiece] = useState<Piece | null>(null);
  const [score, setScore] = useState(0);
  const [lines, setLines] = useState(0);
  const [level, setLevel] = useState(1);
  const [gameOver, setGameOver] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [dropTime, setDropTime] = useState(1000);

  const dropTimeRef = useRef(dropTime);
  const gameLoopRef = useRef<NodeJS.Timeout | null>(null);

  const createRandomPiece = useCallback((): Piece => {
    const tetrominoKeys = Object.keys(
      TETROMINOS
    ) as (keyof typeof TETROMINOS)[];
    const randomKey =
      tetrominoKeys[Math.floor(Math.random() * tetrominoKeys.length)];
    const tetromino = TETROMINOS[randomKey];

    return {
      shape: tetromino.shape,
      color: tetromino.color,
      x:
        Math.floor(BOARD_WIDTH / 2) - Math.floor(tetromino.shape[0].length / 2),
      y: 0,
    };
  }, []);

  const rotatePiece = (piece: Piece): Piece => {
    const rotated = piece.shape[0].map((_, index) =>
      piece.shape.map((row) => row[index]).reverse()
    );
    return { ...piece, shape: rotated };
  };

  const isValidPosition = useCallback(
    (piece: Piece, board: (string | null)[][]): boolean => {
      for (let y = 0; y < piece.shape.length; y++) {
        for (let x = 0; x < piece.shape[y].length; x++) {
          if (piece.shape[y][x]) {
            const newX = piece.x + x;
            const newY = piece.y + y;

            if (
              newX < 0 ||
              newX >= BOARD_WIDTH ||
              newY >= BOARD_HEIGHT ||
              (newY >= 0 && board[newY][newX])
            ) {
              return false;
            }
          }
        }
      }
      return true;
    },
    []
  );

  const placePiece = useCallback(
    (piece: Piece, board: (string | null)[][]): (string | null)[][] => {
      const newBoard = board.map((row) => [...row]);

      for (let y = 0; y < piece.shape.length; y++) {
        for (let x = 0; x < piece.shape[y].length; x++) {
          if (piece.shape[y][x]) {
            const boardY = piece.y + y;
            const boardX = piece.x + x;
            if (boardY >= 0) {
              newBoard[boardY][boardX] = piece.color;
            }
          }
        }
      }

      return newBoard;
    },
    []
  );

  const clearLines = useCallback(
    (
      board: (string | null)[][]
    ): { newBoard: (string | null)[][]; linesCleared: number } => {
      const newBoard = board.filter((row) => row.some((cell) => cell === null));
      const linesCleared = BOARD_HEIGHT - newBoard.length;

      while (newBoard.length < BOARD_HEIGHT) {
        newBoard.unshift(Array(BOARD_WIDTH).fill(null));
      }

      return { newBoard, linesCleared };
    },
    []
  );

  const movePiece = useCallback(
    (dx: number, dy: number) => {
      if (!currentPiece || gameOver || isPaused) return;

      const newPiece = {
        ...currentPiece,
        x: currentPiece.x + dx,
        y: currentPiece.y + dy,
      };

      if (isValidPosition(newPiece, board)) {
        setCurrentPiece(newPiece);
      } else if (dy > 0) {
        // Piece hit bottom, place it
        const newBoard = placePiece(currentPiece, board);
        const { newBoard: clearedBoard, linesCleared } = clearLines(newBoard);

        setBoard(clearedBoard);
        setLines((prev) => prev + linesCleared);
        setScore((prev) => prev + linesCleared * 100 * level);

        // Check if game is over
        if (nextPiece && !isValidPosition(nextPiece, clearedBoard)) {
          setGameOver(true);
        } else {
          setCurrentPiece(nextPiece);
          setNextPiece(createRandomPiece());
        }
      }
    },
    [
      currentPiece,
      board,
      gameOver,
      isPaused,
      isValidPosition,
      placePiece,
      clearLines,
      nextPiece,
      level,
      createRandomPiece,
    ]
  );

  const rotatePieceHandler = useCallback(() => {
    if (!currentPiece || gameOver || isPaused) return;

    const rotated = rotatePiece(currentPiece);
    if (isValidPosition(rotated, board)) {
      setCurrentPiece(rotated);
    }
  }, [currentPiece, board, gameOver, isPaused, isValidPosition]);

  const dropPiece = useCallback(() => {
    movePiece(0, 1);
  }, [movePiece]);

  const hardDrop = useCallback(() => {
    if (!currentPiece || gameOver || isPaused) return;

    let newY = currentPiece.y;
    while (isValidPosition({ ...currentPiece, y: newY + 1 }, board)) {
      newY++;
    }
    setCurrentPiece({ ...currentPiece, y: newY });
    setTimeout(() => dropPiece(), 50);
  }, [currentPiece, board, gameOver, isPaused, isValidPosition, dropPiece]);

  const resetGame = () => {
    setBoard(
      Array(BOARD_HEIGHT)
        .fill(null)
        .map(() => Array(BOARD_WIDTH).fill(null))
    );
    setCurrentPiece(createRandomPiece());
    setNextPiece(createRandomPiece());
    setScore(0);
    setLines(0);
    setLevel(1);
    setGameOver(false);
    setIsPaused(false);
    setDropTime(1000);
  };

  // Initialize game
  useEffect(() => {
    setCurrentPiece(createRandomPiece());
    setNextPiece(createRandomPiece());
  }, [createRandomPiece]);

  // Update drop time based on level
  useEffect(() => {
    const newLevel = Math.floor(lines / 10) + 1;
    if (newLevel !== level) {
      setLevel(newLevel);
      setDropTime(Math.max(100, 1000 - (newLevel - 1) * 100));
    }
  }, [lines, level]);

  // Game loop
  useEffect(() => {
    dropTimeRef.current = dropTime;
  }, [dropTime]);

  useEffect(() => {
    if (!gameOver && !isPaused) {
      gameLoopRef.current = setInterval(() => {
        dropPiece();
      }, dropTimeRef.current);
    }

    return () => {
      if (gameLoopRef.current) {
        clearInterval(gameLoopRef.current);
      }
    };
  }, [dropPiece, gameOver, isPaused]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (gameOver) return;

      switch (e.key) {
        case "ArrowLeft":
          e.preventDefault();
          movePiece(-1, 0);
          break;
        case "ArrowRight":
          e.preventDefault();
          movePiece(1, 0);
          break;
        case "ArrowDown":
          e.preventDefault();
          dropPiece();
          break;
        case "ArrowUp":
          e.preventDefault();
          rotatePieceHandler();
          break;
        case " ":
          e.preventDefault();
          hardDrop();
          break;
        case "p":
        case "P":
          e.preventDefault();
          setIsPaused((prev) => !prev);
          break;
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [movePiece, dropPiece, rotatePieceHandler, hardDrop, gameOver]);

  // Render board with current piece
  const renderBoard = () => {
    const displayBoard = board.map((row) => [...row]);

    // Add current piece to display board
    if (currentPiece) {
      for (let y = 0; y < currentPiece.shape.length; y++) {
        for (let x = 0; x < currentPiece.shape[y].length; x++) {
          if (currentPiece.shape[y][x]) {
            const boardY = currentPiece.y + y;
            const boardX = currentPiece.x + x;
            if (
              boardY >= 0 &&
              boardY < BOARD_HEIGHT &&
              boardX >= 0 &&
              boardX < BOARD_WIDTH
            ) {
              displayBoard[boardY][boardX] = currentPiece.color;
            }
          }
        }
      }
    }

    return displayBoard
      .flat()
      .map((cell, index) => (
        <Cell key={index} filled={!!cell} color={cell || undefined} />
      ));
  };

  // Render next piece
  const renderNextPiece = () => {
    if (!nextPiece) return null;

    const maxSize = Math.max(
      nextPiece.shape.length,
      nextPiece.shape[0]?.length || 0
    );
    const grid = Array(maxSize)
      .fill(null)
      .map(() => Array(maxSize).fill(false));

    for (let y = 0; y < nextPiece.shape.length; y++) {
      for (let x = 0; x < nextPiece.shape[y].length; x++) {
        if (nextPiece.shape[y][x]) {
          grid[y][x] = true;
        }
      }
    }

    return (
      <NextPieceGrid size={maxSize}>
        {grid.flat().map((cell, index) => (
          <NextPieceCell
            key={index}
            filled={cell}
            color={cell ? nextPiece.color : undefined}
          />
        ))}
      </NextPieceGrid>
    );
  };

  return (
    <ThemeProvider theme={token}>
      <GameContainer>
        <GameBoard>{renderBoard()}</GameBoard>

        <GameInfo>
          <ScoreDisplay>Score: {score}</ScoreDisplay>

          <ScoreDisplay>Lines: {lines}</ScoreDisplay>

          <ScoreDisplay>Level: {level}</ScoreDisplay>

          <NextPieceContainer>
            <div>Next:</div>
            {renderNextPiece()}
          </NextPieceContainer>

          <ControlButton
            onClick={() => setIsPaused(!isPaused)}
            disabled={gameOver}
          >
            {isPaused ? "Resume" : "Pause"}
          </ControlButton>

          <ControlButton onClick={resetGame}>New Game</ControlButton>

          {gameOver && (
            <ScoreDisplay style={{ color: "red" }}>Game Over!</ScoreDisplay>
          )}

          <div style={{ fontSize: "10px", marginTop: "10px" }}>
            <div>Controls:</div>
            <div>← → Move</div>
            <div>↓ Soft Drop</div>
            <div>↑ Rotate</div>
            <div>Space Hard Drop</div>
            <div>P Pause</div>
          </div>
        </GameInfo>
      </GameContainer>
    </ThemeProvider>
  );
};

export default Tetris;
